import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, Horse, PuzzlePiece, Dog, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fc = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const CLASS_CONFIG = {
  estrela: { icon: Star, label: "Estrela", desc: "Alta popularidade + Alta lucratividade", color: "border-amber-300 bg-amber-50", text: "text-amber-800", badge: "bg-amber-200 text-amber-900" },
  burro_carga: { icon: Horse, label: "Burro de Carga", desc: "Alta popularidade + Baixa lucratividade", color: "border-blue-300 bg-blue-50", text: "text-blue-800", badge: "bg-blue-200 text-blue-900" },
  quebra_cabeca: { icon: PuzzlePiece, label: "Quebra-cabeça", desc: "Baixa popularidade + Alta lucratividade", color: "border-purple-300 bg-purple-50", text: "text-purple-800", badge: "bg-purple-200 text-purple-900" },
  cachorro: { icon: Dog, label: "Cachorro", desc: "Baixa popularidade + Baixa lucratividade", color: "border-zinc-300 bg-zinc-50", text: "text-zinc-700", badge: "bg-zinc-200 text-zinc-700" },
};

export default function MenuEngineering() {
  const [data, setData] = useState(null);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/menu-engineering`).then(r => setData(r.data))
      .catch(() => toast.error("Erro ao carregar"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  if (!data || data.items.length === 0) {
    return (
      <div className="space-y-6" data-testid="menu-engineering-page">
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("me_title")}</h2>
        <Card><CardContent className="text-center py-12">
          <Star size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">{t("me_no_data")}</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-engineering-page">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("me_title")}</h2>
        <p className="text-sm text-zinc-500 mt-1">{t("me_subtitle")}</p>
      </div>

      {/* Classification summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(CLASS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const count = data.classifications[key] || 0;
          return (
            <Card key={key} className={`border-2 shadow-sm ${config.color}`}>
              <CardContent className="pt-4 pb-3 px-4 text-center">
                <Icon size={28} weight="fill" className={`mx-auto mb-1 ${config.text}`} />
                <p className={`text-2xl font-bold ${config.text}`}>{count}</p>
                <p className={`text-xs font-semibold ${config.text}`}>{config.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Items by classification */}
      {Object.entries(CLASS_CONFIG).map(([key, config]) => {
        const items = data.items.filter(i => i.classification === key);
        if (items.length === 0) return null;
        const Icon = config.icon;
        return (
          <Card key={key} className={`border shadow-sm ${config.color}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-bold flex items-center gap-2 ${config.text}`}>
                <Icon size={18} weight="fill" /> {config.label}
                <span className="text-xs font-normal">— {config.desc}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.sheet_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-200">
                    <div>
                      <p className="text-sm font-bold text-zinc-900">{item.name}</p>
                      <p className="text-xs text-zinc-500">{item.qty_sold} vendido(s) · Margem: {item.margin}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-bold text-zinc-900">{fc(item.profit)}</p>
                      <p className="text-xs text-zinc-500">lucro total</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
